import React, { useCallback, useEffect, useState } from "react";
import api, { formatApiError, formatTanggal } from "@/lib/api";
import Layout, { PageTitle } from "@/components/Layout";
import { NAV_KEPSEK } from "@/lib/nav";
import { FilterBar } from "@/components/FilterBar";
import { toast } from "sonner";
import { MessageSquarePlus, X } from "lucide-react";

const F_EMPTY = { tanggal: "", bulan: "", tahun: "", status: "" };

export function KendalaPage({ nav = NAV_KEPSEK, dataTestidPrefix = "kepsek-kendala-filter" }) {
  const [all, setAll] = useState([]);
  const [filters, setFilters] = useState(F_EMPTY);
  const [applied, setApplied] = useState(F_EMPTY);
  const [replyFor, setReplyFor] = useState(null);
  const [replyText, setReplyText] = useState("");
  const [saving, setSaving] = useState(false);

  const load = useCallback(async () => {
    try { const { data } = await api.get("/kendala"); setAll(data); }
    catch (e) { console.error(e); }
  }, []);
  useEffect(() => { load(); }, [load]);

  const items = all.filter((k) => {
    if (applied.tanggal && k.tanggal !== applied.tanggal) return false;
    if (applied.bulan && (k.tanggal || "").slice(5, 7) !== applied.bulan) return false;
    if (applied.tahun && (k.tanggal || "").slice(0, 4) !== String(applied.tahun)) return false;
    if (applied.status && k.status !== applied.status) return false;
    return true;
  });

  const openReply = (k) => {
    setReplyFor(k);
    setReplyText(k.tanggapan || "");
  };

  const submitReply = async () => {
    if (!replyText.trim()) { toast.error("Tanggapan tidak boleh kosong"); return; }
    setSaving(true);
    try {
      await api.post(`/kendala/${replyFor.id}/tanggapan`, { tanggapan: replyText.trim() });
      toast.success("Tanggapan tersimpan");
      setReplyFor(null);
      setReplyText("");
      load();
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
    } finally { setSaving(false); }
  };

  return (
    <Layout nav={nav}>
      <PageTitle title="Kendala Guru" />
      <FilterBar
        filters={filters} setFilters={setFilters}
        onApply={() => setApplied({ ...filters })}
        onReset={() => { setFilters(F_EMPTY); setApplied(F_EMPTY); }}
        dataTestidPrefix={dataTestidPrefix}
        fields={[
          { key: "tanggal", type: "tanggal", label: "Tanggal" },
          { key: "bulan", type: "bulan", label: "Bulan" },
          { key: "tahun", type: "tahun", label: "Tahun" },
          { key: "status", type: "select", label: "Status", options: [{ v: "belum_ditangani", label: "Belum Ditangani" }, { v: "sudah_ditangani", label: "Sudah Ditangani" }] },
        ]}
      />

      {replyFor && (
        <section className="pp-card p-6 mb-6 animate-fade-up" data-testid="kendala-reply-form">
          <div className="flex items-center justify-between mb-4">
            <h3 className="text-lg font-semibold">Beri Tanggapan — {replyFor.nama_guru}</h3>
            <button onClick={() => { setReplyFor(null); setReplyText(""); }} data-testid="kendala-reply-close">
              <X className="w-5 h-5 text-slate-400" />
            </button>
          </div>
          <div className="mb-3 p-3 bg-slate-50 rounded text-sm text-slate-700 whitespace-pre-wrap border border-slate-200">
            <div className="text-xs text-slate-500 mb-1">Isi Kendala ({formatTanggal(replyFor.tanggal)}):</div>
            {replyFor.isi}
          </div>
          <label className="pp-label">Tanggapan Anda *</label>
          <textarea
            rows={4}
            className="pp-input"
            value={replyText}
            onChange={(e) => setReplyText(e.target.value)}
            placeholder="Tulis tanggapan / solusi untuk kendala ini…"
            data-testid="kendala-reply-textarea"
          />
          <div className="mt-4 flex justify-end gap-2">
            <button onClick={() => { setReplyFor(null); setReplyText(""); }} className="pp-btn-secondary">Batal</button>
            <button
              onClick={submitReply}
              disabled={saving}
              className="pp-btn-primary disabled:opacity-60"
              data-testid="kendala-reply-save"
            >
              {saving ? "Menyimpan…" : "Kirim Tanggapan"}
            </button>
          </div>
        </section>
      )}

      {/* Tabel — desktop saja */}
      <section className="pp-card pp-freeze overflow-hidden hidden md:block">
        <div className="overflow-x-auto">
          <table className="min-w-[1000px] w-full text-sm">
            <thead><tr className="pp-table-header">
              <th className="text-left px-5 py-3">Tanggal</th>
              <th className="text-left px-5 py-3">Nama Guru</th>
              <th className="text-left px-5 py-3">Isi Kendala</th>
              <th className="text-left px-5 py-3">Tanggapan</th>
              <th className="text-left px-5 py-3">Status</th>
              <th className="text-right px-5 py-3">Aksi</th>
            </tr></thead>
            <tbody>
              {items.length === 0 && <tr><td colSpan={6} className="text-center py-10 text-slate-400 italic">Belum ada kendala.</td></tr>}
              {items.map((k) => (
                <tr key={k.id} className="pp-row align-top">
                  <td className="px-5 py-3 font-mono text-xs">{formatTanggal(k.tanggal)}</td>
                  <td className="px-5 py-3 font-medium">{k.nama_guru}</td>
                  <td className="px-5 py-3 text-slate-700 max-w-[300px] whitespace-pre-wrap">{k.isi}</td>
                  <td className="px-5 py-3 text-slate-700 max-w-[300px] whitespace-pre-wrap">
                    {k.tanggapan ? (
                      <div>
                        <div className="text-slate-800">{k.tanggapan}</div>
                        <div className="text-[11px] text-slate-500 mt-1">— {k.tanggapan_oleh} ({k.tanggapan_role}), {formatTanggal((k.tanggapan_at || "").slice(0, 10))}</div>
                      </div>
                    ) : <span className="italic text-slate-400">Belum ada tanggapan</span>}
                  </td>
                  <td className="px-5 py-3"><span className={k.status === "sudah_ditangani" ? "pp-badge-success" : "pp-badge-warning"}>{k.status === "sudah_ditangani" ? "Sudah Ditangani" : "Belum Ditangani"}</span></td>
                  <td className="px-5 py-3 text-right">
                    <button onClick={() => openReply(k)} className="pp-btn-primary" data-testid={`kendala-reply-btn-${k.id}`}>
                      <MessageSquarePlus className="w-3.5 h-3.5" />
                      {k.tanggapan ? "Ubah Tanggapan" : "Beri Tanggapan"}
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </section>
      {/* Kartu — mobile saja */}
      <section className="md:hidden space-y-3">
        {items.length === 0 && (
          <div className="pp-card p-8 text-center text-slate-400 italic">Belum ada kendala.</div>
        )}
        {items.map((k) => (
          <div key={k.id} className="pp-card p-4" data-testid={`kendala-card-${k.id}`}>
            <div className="flex items-start justify-between gap-2 mb-2">
              <div>
                <div className="font-semibold text-slate-900 text-sm">{k.nama_guru}</div>
                <div className="text-[11px] text-slate-500 font-mono">{formatTanggal(k.tanggal)}</div>
              </div>
              <span className={k.status === "sudah_ditangani" ? "pp-badge-success" : "pp-badge-warning"}>
                {k.status === "sudah_ditangani" ? "Sudah Ditangani" : "Belum Ditangani"}
              </span>
            </div>
            <div className="text-sm text-slate-700 whitespace-pre-wrap bg-slate-50 rounded-lg p-3 border border-slate-100">{k.isi}</div>
            {k.tanggapan ? (
              <div className="mt-2 text-sm text-slate-700 whitespace-pre-wrap bg-emerald-50 rounded-lg p-3 border border-emerald-100">
                <div className="text-[11px] font-semibold text-emerald-700 mb-1">Tanggapan:</div>
                {k.tanggapan}
                <div className="text-[11px] text-slate-500 mt-1.5">— {k.tanggapan_oleh} ({k.tanggapan_role}), {formatTanggal((k.tanggapan_at || "").slice(0, 10))}</div>
              </div>
            ) : (
              <div className="mt-2 text-xs italic text-slate-400">Belum ada tanggapan</div>
            )}
            <button onClick={() => openReply(k)} className="pp-btn-primary w-full mt-3" data-testid={`kendala-card-reply-${k.id}`}>
              <MessageSquarePlus className="w-3.5 h-3.5" />
              {k.tanggapan ? "Ubah Tanggapan" : "Beri Tanggapan"}
            </button>
          </div>
        ))}
      </section>
    </Layout>
  );
}

export default function KendalaKepsek() {
  return <KendalaPage nav={NAV_KEPSEK} dataTestidPrefix="kepsek-kendala-filter" />;
}
